import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";

export default function RegisterPage() {
  const { t } = useI18n();
  const { register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "customer" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register(form);
      const dest = u.role === "artist" ? "/artist" : "/";
      navigate(dest);
      toast.success(`Welcome ${u.name}`);
    } catch (err) {
      const msg = formatApiError(err);
      if (err?.response?.status === 400 && msg.toLowerCase().includes("already")) {
        toast.error("Este email ya está registrado. Te llevamos al login.", {
          action: { label: "Ir a login", onClick: () => navigate(`/login?email=${encodeURIComponent(form.email)}`) },
        });
        setTimeout(() => navigate(`/login?email=${encodeURIComponent(form.email)}`), 2500);
      } else {
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full border border-neutral-300 px-4 py-3 outline-none focus:border-black bg-white text-sm";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 font-sans">
      <div className="flex flex-col px-6 py-10 lg:px-16 lg:py-16 order-2 lg:order-1">
        <Link to="/" className="overline text-neutral-500 hover:text-black flex items-center gap-2" data-testid="back-home">
          <ArrowLeft size={14} /> d-Pod Gallery
        </Link>
        <div className="my-auto max-w-md w-full mx-auto">
          <h1 className="font-serif text-5xl font-light tracking-tight">{t.auth.register}</h1>
          <form onSubmit={submit} className="mt-10 space-y-4" data-testid="register-form">
            <input required placeholder={t.auth.name} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} data-testid="register-name" />
            <input required type="email" placeholder={t.auth.email} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} data-testid="register-email" />
            <input required type="password" minLength={6} placeholder={t.auth.password} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} data-testid="register-password" />
            <div>
              <div className="overline text-neutral-500 mb-2">{t.auth.role}</div>
              <div className="grid grid-cols-2 gap-3">
                <button type="button" onClick={() => setForm({ ...form, role: "customer" })} className={`py-3 overline border ${form.role === "customer" ? "border-black bg-black text-white" : "border-neutral-300 hover:bg-neutral-100"}`} data-testid="role-customer">
                  {t.auth.customer}
                </button>
                <button type="button" onClick={() => setForm({ ...form, role: "artist" })} className={`py-3 overline border ${form.role === "artist" ? "border-black bg-black text-white" : "border-neutral-300 hover:bg-neutral-100"}`} data-testid="role-artist">
                  {t.auth.artist}
                </button>
              </div>
            </div>
            <button disabled={loading} type="submit" className="w-full bg-black text-white py-4 overline hover:bg-neutral-800 transition-colors disabled:opacity-50" data-testid="register-submit">
              {loading ? "..." : t.auth.submit}
            </button>
          </form>
          <p className="mt-8 text-sm text-neutral-500">
            {t.auth.hasAccount} <Link to="/login" className="text-black underline" data-testid="go-login">{t.auth.login}</Link>
          </p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-neutral-100 order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1618331833071-ce81bd50d300?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80" alt="" className="absolute inset-0 w-full h-full object-cover" />
      </div>
    </div>
  );
}
